import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { automapDealerPortalPath } from "../../constants/automapDealerPortalRoutes";
import { trackFeaturedPlacementEvent } from "../../constants/automapFeaturedPlacementConstants";
import { useDealerPortalIntl } from "../../hooks/useDealerPortalIntl";
import { useCurrency, useLocale } from "../../intl";
import { fetchDealerSponsorships, requestFeaturedPlacement } from "../../services/automapFeaturedPlacementApi";

const BOOST_VEHICLE_PAGE_SIZE = 12;

function formatDateRange(startAt, endAt, locale) {
  if (!startAt && !endAt) return "—";
  const start = startAt ? new Date(startAt).toLocaleDateString(locale) : "—";
  const end = endAt ? new Date(endAt).toLocaleDateString(locale) : "—";
  return `${start} – ${end}`;
}

function placementStatusClass(status) {
  if (status === "active") return "dealer-badge dealer-badge--success";
  if (status === "scheduled") return "dealer-badge dealer-badge--info";
  if (status === "paused") return "dealer-badge dealer-badge--warning";
  return "dealer-badge";
}

function boostTypeLabel(t, placementType, fallback) {
  const key = `sponsorships.boostTypes.${placementType}.label`;
  const translated = t(key);
  if (!translated || translated === key || translated.startsWith("[MISSING:")) {
    return fallback || placementType;
  }
  return translated;
}

function boostTypeDescription(t, placementType, fallback) {
  const key = `sponsorships.boostTypes.${placementType}.description`;
  const translated = t(key);
  if (!translated || translated === key || translated.startsWith("[MISSING:")) {
    return fallback || "";
  }
  return translated;
}

function filterBoostableListings(listings, { search, filter, sort, locale }) {
  let rows = listings;
  const query = search.trim().toLowerCase();

  if (query) {
    rows = rows.filter(
      (listing) =>
        listing.vehicle?.toLowerCase().includes(query) || listing.city?.toLowerCase().includes(query)
    );
  }

  if (filter === "available") {
    rows = rows.filter((listing) => !listing.isSponsored);
  } else if (filter === "boosted") {
    rows = rows.filter((listing) => listing.isSponsored);
  }

  return [...rows].sort((left, right) => {
    if (sort === "price_asc") {
      return (left.priceCop || 0) - (right.priceCop || 0);
    }
    if (sort === "price_desc") {
      return (right.priceCop || 0) - (left.priceCop || 0);
    }
    return (left.vehicle || "").localeCompare(right.vehicle || "", locale, { sensitivity: "base" });
  });
}

export default function DealerSponsorshipsPage() {
  const { dealerId } = useOutletContext();
  const { locale } = useLocale();
  const { formatMoney } = useCurrency();
  const { t, translateError, localizeFeatured, formatPlacementStatus } = useDealerPortalIntl();
  const [searchParams] = useSearchParams();
  const boostFormRef = useRef(null);
  const vehicleListRef = useRef(null);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedListingId, setSelectedListingId] = useState(searchParams.get("listingId") || "");
  const [selectedBoostType, setSelectedBoostType] = useState("search_top_slot");
  const [requestMessage, setRequestMessage] = useState("");
  const [requestStatus, setRequestStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("all");
  const [vehicleSort, setVehicleSort] = useState("vehicle_asc");
  const [vehiclePage, setVehiclePage] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchDealerSponsorships(dealerId);
      setPayload(data.sponsorships);
      trackFeaturedPlacementEvent("dealer_sponsorships_viewed", { dealerId });
    } catch (err) {
      setError(translateError(err.message, "loadSponsorships"));
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [dealerId, translateError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const listingId = searchParams.get("listingId");
    if (listingId) {
      setSelectedListingId(listingId);
      boostFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [searchParams]);

  const sponsorships = useMemo(
    () => (payload ? localizeFeatured(payload) : null),
    [payload, localizeFeatured]
  );

  const boostOptions = useMemo(() => sponsorships?.boostOptions || [], [sponsorships]);
  const boostableListings = useMemo(() => sponsorships?.boostableListings || [], [sponsorships]);

  const filteredBoostableListings = useMemo(
    () =>
      filterBoostableListings(boostableListings, {
        search: vehicleSearch,
        filter: vehicleFilter,
        sort: vehicleSort,
        locale,
      }),
    [boostableListings, vehicleFilter, vehicleSearch, vehicleSort, locale]
  );

  const vehiclePageCount = Math.max(1, Math.ceil(filteredBoostableListings.length / BOOST_VEHICLE_PAGE_SIZE));

  const paginatedBoostableListings = useMemo(() => {
    const start = vehiclePage * BOOST_VEHICLE_PAGE_SIZE;
    return filteredBoostableListings.slice(start, start + BOOST_VEHICLE_PAGE_SIZE);
  }, [filteredBoostableListings, vehiclePage]);

  const selectedListing = useMemo(
    () => boostableListings.find((row) => row.listingId === selectedListingId) || null,
    [boostableListings, selectedListingId]
  );

  const selectedBoostOption = useMemo(
    () => boostOptions.find((option) => option.placementType === selectedBoostType) || boostOptions[0],
    [boostOptions, selectedBoostType]
  );

  const spendSummary = sponsorships?.spendSummary;
  const newBoostEstimateCop = selectedBoostOption?.estimatedCostCop || 0;
  const totalAfterRequestCop = (spendSummary?.totalCommittedCop || 0) + newBoostEstimateCop;

  useEffect(() => {
    setVehiclePage(0);
  }, [vehicleSearch, vehicleFilter, vehicleSort]);

  useEffect(() => {
    if (!selectedListingId || filteredBoostableListings.length === 0) return;
    const index = filteredBoostableListings.findIndex((listing) => listing.listingId === selectedListingId);
    if (index >= 0) {
      setVehiclePage(Math.floor(index / BOOST_VEHICLE_PAGE_SIZE));
    }
  }, [filteredBoostableListings, selectedListingId]);

  useEffect(() => {
    if (vehiclePage > vehiclePageCount - 1) {
      setVehiclePage(Math.max(0, vehiclePageCount - 1));
    }
  }, [vehiclePage, vehiclePageCount]);

  const selectVehicle = (listingId, { scroll = true } = {}) => {
    setSelectedListingId(listingId);
    setRequestStatus("");
    if (scroll) {
      vehicleListRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  const submitBoostRequest = async (event) => {
    event.preventDefault();
    if (!selectedListingId) {
      setRequestStatus(t("sponsorships.selectListingRequired"));
      return;
    }
    const listing = boostableListings.find((row) => row.listingId === selectedListingId);
    if (listing && !listing.canBoost) {
      setRequestStatus(t("sponsorships.cannotBoostInactive"));
      return;
    }
    setSubmitting(true);
    setRequestStatus("");
    try {
      await requestFeaturedPlacement(dealerId, {
        listingId: selectedListingId,
        requestedPlacementType: selectedBoostType,
        message: requestMessage.trim() || t("sponsorships.defaultRequestMessage"),
      });
      trackFeaturedPlacementEvent("dealer_featured_request_created", {
        dealerId,
        listingId: selectedListingId,
        placementType: selectedBoostType,
      });
      setRequestStatus(t("sponsorships.requestSent"));
      setRequestMessage("");
      await load();
    } catch (err) {
      setRequestStatus(translateError(err.message, "generic"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <p className="dealer-state-box">{t("sponsorships.loading")}</p>;
  }

  if (error) {
    return (
      <div className="dealer-state-box dealer-panel--error">
        <p>{error}</p>
        <button type="button" className="dealer-portal-btn primary" onClick={load}>
          {t("common.retry")}
        </button>
      </div>
    );
  }

  if (!sponsorships) return null;

  const {
    featuredEligible,
    upgradeMessage,
    performance,
    popularListings = [],
    popularSummary,
    activePlacements = [],
    scheduledPlacements = [],
    recentPlacements = [],
    pendingRequests = [],
  } = sponsorships;

  const visibleVehicleCount = paginatedBoostableListings.length;

  return (
    <div className="dealer-sponsorships-page">
      <div className="dealer-sponsorships-page__header import-page-header">
        <div>
          <h2>{t("sponsorships.title")}</h2>
          <p className="muted">{t("sponsorships.subtitle")}</p>
        </div>
        <div className="import-actions">
          <Link className="dealer-portal-btn" to={automapDealerPortalPath("plan")}>
            {t("sponsorships.viewPlan")}
          </Link>
          <Link className="dealer-portal-btn" to={automapDealerPortalPath("reportes")}>
            {t("dashboard.reports")}
          </Link>
        </div>
      </div>

      {!featuredEligible && upgradeMessage ? (
        <section className="dealer-panel dealer-panel--insight">
          <p>{upgradeMessage}</p>
          <Link className="dealer-portal-btn primary" to={automapDealerPortalPath("plan")}>
            {t("sponsorships.upgradeCta")}
          </Link>
        </section>
      ) : null}

      <section className="dealer-panel dealer-sponsorships-overview" aria-label={t("sponsorships.spendTitle")}>
        <div className="dealer-sponsorships-overview__spend">
          <div>
            <h3>{t("sponsorships.spendTitle")}</h3>
            <p className="muted dealer-sponsorships-page__spend-hint">{t("sponsorships.spendHint")}</p>
          </div>
          <div className="dealer-sponsorships-page__spend-total">
            <span className="dealer-sponsorships-page__spend-total-label">{t("sponsorships.spendTotal")}</span>
            <strong>{formatMoney(spendSummary?.totalCommittedCop || 0)}</strong>
          </div>
        </div>

        {spendSummary?.totalCommittedCop > 0 ? (
          <div className="dealer-sponsorships-page__spend-grid dealer-sponsorships-overview__breakdown">
            <article className="dealer-sponsorships-page__spend-card">
              <span>{t("sponsorships.spendActive")}</span>
              <strong>{formatMoney(spendSummary.activeCommittedCop || 0)}</strong>
              <small>{spendSummary.activeBoostCount || 0}</small>
            </article>
            <article className="dealer-sponsorships-page__spend-card">
              <span>{t("sponsorships.spendScheduled")}</span>
              <strong>{formatMoney(spendSummary.scheduledCommittedCop || 0)}</strong>
              <small>{spendSummary.scheduledBoostCount || 0}</small>
            </article>
            <article className="dealer-sponsorships-page__spend-card">
              <span>{t("sponsorships.spendPending")}</span>
              <strong>{formatMoney(spendSummary.pendingRequestsCop || 0)}</strong>
              <small>{spendSummary.pendingRequestCount || 0}</small>
            </article>
          </div>
        ) : (
          <p className="muted dealer-sponsorships-overview__empty">{t("sponsorships.spendEmpty")}</p>
        )}

        <div className="dealer-sponsorships-overview__kpis">
          <article className="dealer-kpi-card dealer-kpi-card--warm">
            <span className="dealer-kpi-card__label">{t("sponsorships.totalLikes")}</span>
            <strong className="dealer-kpi-card__value">{popularSummary?.totalSaves ?? 0}</strong>
            <small className="dealer-kpi-card__hint">
              {t("sponsorships.likedVehicles", { count: popularSummary?.totalListingsWithSaves ?? 0 })}
            </small>
          </article>
          <article className="dealer-kpi-card dealer-kpi-card--blue">
            <span className="dealer-kpi-card__label">{t("sponsorships.activeBoosts")}</span>
            <strong className="dealer-kpi-card__value">{activePlacements.length}</strong>
          </article>
          <article className="dealer-kpi-card">
            <span className="dealer-kpi-card__label">{t("sponsorships.pendingRequests")}</span>
            <strong className="dealer-kpi-card__value">{pendingRequests.length}</strong>
          </article>
          <article className="dealer-kpi-card">
            <span className="dealer-kpi-card__label">{t("reports.impressions")}</span>
            <strong className="dealer-kpi-card__value">{performance?.impressions ?? 0}</strong>
          </article>
        </div>
      </section>

      <div className="dealer-sponsorships-workspace">
        <section className="dealer-panel dealer-sponsorships-page__request" ref={boostFormRef}>
          <div className="dealer-panel__header">
            <div>
              <h3>{t("sponsorships.manageTitle")}</h3>
              <p className="muted dealer-sponsorships-page__manage-intro">{t("sponsorships.manageIntro")}</p>
            </div>
          </div>

          <ol className="dealer-boost-steps" aria-label={t("sponsorships.manageTitle")}>
            <li className={`dealer-boost-steps__item${selectedListing ? " is-complete" : " is-current"}`}>
              {t("sponsorships.stepVehicle")}
            </li>
            <li
              className={`dealer-boost-steps__item${
                selectedListing && selectedBoostOption ? " is-complete" : selectedListing ? " is-current" : ""
              }`}
            >
              {t("sponsorships.stepPlacement")}
            </li>
            <li
              className={`dealer-boost-steps__item${
                selectedListing && selectedBoostOption ? " is-current" : ""
              }`}
            >
              {t("sponsorships.stepReview")}
            </li>
          </ol>

          <form className="dealer-boost-form" onSubmit={submitBoostRequest}>
            <fieldset className="dealer-boost-form__section">
              <legend>{t("sponsorships.selectVehicle")}</legend>
              <p className="muted dealer-boost-form__hint">{t("sponsorships.selectVehicleHint")}</p>

              {boostableListings.length === 0 ? (
                <div className="dealer-sponsorships-page__empty-vehicles">
                  <p className="muted">{t("sponsorships.noBoostableVehicles")}</p>
                  <Link className="dealer-portal-btn primary" to={automapDealerPortalPath("inventario/nuevo")}>
                    {t("sponsorships.addInventoryCta")}
                  </Link>
                </div>
              ) : (
                <>
                  <div className="dealer-boost-vehicle-toolbar">
                    <label className="dealer-boost-vehicle-toolbar__search">
                      <span className="dealer-boost-vehicle-toolbar__label">{t("inventory.searchPlaceholder")}</span>
                      <input
                        type="search"
                        value={vehicleSearch}
                        onChange={(event) => setVehicleSearch(event.target.value)}
                        placeholder={t("sponsorships.vehicleSearchPlaceholder")}
                      />
                    </label>
                    <label className="dealer-boost-vehicle-toolbar__filter">
                      <span className="dealer-boost-vehicle-toolbar__label">{t("sponsorships.vehicleFilterLabel")}</span>
                      <select value={vehicleFilter} onChange={(event) => setVehicleFilter(event.target.value)}>
                        <option value="all">{t("sponsorships.vehicleFilterAll")}</option>
                        <option value="available">{t("sponsorships.vehicleFilterAvailable")}</option>
                        <option value="boosted">{t("sponsorships.vehicleFilterBoosted")}</option>
                      </select>
                    </label>
                    <label className="dealer-boost-vehicle-toolbar__filter">
                      <span className="dealer-boost-vehicle-toolbar__label">{t("sponsorships.vehicleSortLabel")}</span>
                      <select value={vehicleSort} onChange={(event) => setVehicleSort(event.target.value)}>
                        <option value="vehicle_asc">{t("sponsorships.vehicleSortAlpha")}</option>
                        <option value="price_asc">{t("sponsorships.vehicleSortPriceAsc")}</option>
                        <option value="price_desc">{t("sponsorships.vehicleSortPriceDesc")}</option>
                      </select>
                    </label>
                  </div>

                  <div className="dealer-boost-vehicle-toolbar__meta">
                    <p className="dealer-boost-vehicle-toolbar__count">
                      {t("sponsorships.vehicleResultsCount", {
                        shown: visibleVehicleCount,
                        total: filteredBoostableListings.length,
                      })}
                    </p>
                    {vehiclePageCount > 1 ? (
                      <p className="dealer-boost-vehicle-toolbar__page muted">
                        {t("sponsorships.paginationPage", {
                          page: vehiclePage + 1,
                          total: vehiclePageCount,
                        })}
                      </p>
                    ) : null}
                  </div>

                  <div
                    className="dealer-boost-vehicle-list"
                    ref={vehicleListRef}
                    role="listbox"
                    aria-label={t("sponsorships.selectVehicle")}
                  >
                    {paginatedBoostableListings.length === 0 ? (
                      <p className="muted dealer-boost-vehicle-list__empty">{t("sponsorships.vehiclePickerEmpty")}</p>
                    ) : (
                      paginatedBoostableListings.map((listing) => {
                        const selected = selectedListingId === listing.listingId;
                        return (
                          <button
                            key={listing.listingId}
                            type="button"
                            role="option"
                            aria-selected={selected}
                            className={`dealer-boost-vehicle-row${selected ? " is-selected" : ""}`}
                            onClick={() => selectVehicle(listing.listingId, { scroll: false })}
                          >
                            <span className="dealer-boost-vehicle-row__thumb">
                              {listing.primaryPhotoUrl ? (
                                <img src={listing.primaryPhotoUrl} alt="" />
                              ) : (
                                <span className="dealer-boost-vehicle-row__placeholder">{t("inventory.noPhoto")}</span>
                              )}
                            </span>
                            <span className="dealer-boost-vehicle-row__body">
                              <strong>{listing.vehicle}</strong>
                              <span className="muted">
                                {formatMoney(listing.priceCop)} · {listing.city || "—"}
                              </span>
                            </span>
                            <span className="dealer-boost-vehicle-row__status">
                              {listing.isSponsored ? (
                                <span className="dealer-badge dealer-badge--success">{t("sponsorships.alreadyBoosted")}</span>
                              ) : (
                                <span className="muted">{t("sponsorships.notBoosted")}</span>
                              )}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>

                  {vehiclePageCount > 1 ? (
                    <div className="dealer-boost-vehicle-pagination">
                      <button
                        type="button"
                        className="dealer-portal-btn"
                        disabled={vehiclePage === 0}
                        onClick={() => setVehiclePage((page) => Math.max(0, page - 1))}
                      >
                        {t("sponsorships.paginationPrev")}
                      </button>
                      <button
                        type="button"
                        className="dealer-portal-btn"
                        disabled={vehiclePage >= vehiclePageCount - 1}
                        onClick={() => setVehiclePage((page) => Math.min(vehiclePageCount - 1, page + 1))}
                      >
                        {t("sponsorships.paginationNext")}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </fieldset>

            {selectedListing ? (
              <div className="dealer-boost-selected-summary">
                <span className="dealer-boost-selected-summary__label">{t("sponsorships.selectedVehicle")}</span>
                <div className="dealer-boost-selected-summary__card">
                  <span className="dealer-boost-selected-summary__thumb">
                    {selectedListing.primaryPhotoUrl ? (
                      <img src={selectedListing.primaryPhotoUrl} alt="" />
                    ) : (
                      <span>{t("inventory.noPhoto")}</span>
                    )}
                  </span>
                  <div className="dealer-boost-selected-summary__meta">
                    <strong>{selectedListing.vehicle}</strong>
                    <span className="muted">
                      {formatMoney(selectedListing.priceCop)} · {selectedListing.city || "—"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="dealer-portal-btn dealer-boost-selected-summary__change"
                    onClick={() => vehicleListRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  >
                    {t("sponsorships.changeVehicle")}
                  </button>
                </div>
              </div>
            ) : null}

            <fieldset className="dealer-boost-form__section">
              <legend>{t("sponsorships.boostType")}</legend>
              <p className="muted dealer-boost-form__hint">{t("sponsorships.boostTypeHint")}</p>
              <div className="dealer-boost-type-grid">
                {boostOptions.map((option) => {
                  const selected = selectedBoostType === option.placementType;
                  return (
                    <label
                      key={option.placementType}
                      className={`dealer-boost-type-card${selected ? " is-selected" : ""}`}
                    >
                      <input
                        type="radio"
                        name="boostType"
                        value={option.placementType}
                        checked={selected}
                        onChange={() => setSelectedBoostType(option.placementType)}
                      />
                      <span className="dealer-boost-type-card__head">
                        <strong>{boostTypeLabel(t, option.placementType, option.label)}</strong>
                        <span className="dealer-boost-type-card__price">
                          {t("sponsorships.pricingPerWeek", {
                            price: formatMoney(option.pricePerWeekCop),
                          })}
                        </span>
                      </span>
                      <span className="dealer-boost-type-card__desc">
                        {boostTypeDescription(t, option.placementType, option.description)}
                      </span>
                      <span className="dealer-boost-type-card__estimate muted">
                        {t("sponsorships.pricingDefaultDuration", { days: option.defaultDurationDays })}
                        {" · "}
                        {t("sponsorships.costEstimated", { price: formatMoney(option.estimatedCostCop) })}
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="dealer-sponsorships-page__pricing-note muted">{t("sponsorships.pricingNote")}</p>
            </fieldset>

            {selectedBoostOption ? (
              <div className="dealer-sponsorships-page__estimate-box">
                <p>
                  {t("sponsorships.newBoostEstimate", {
                    price: formatMoney(newBoostEstimateCop),
                    days: selectedBoostOption.defaultDurationDays,
                  })}
                </p>
                <p className="dealer-sponsorships-page__estimate-total">
                  {t("sponsorships.newBoostTotalAfter", { price: formatMoney(totalAfterRequestCop) })}
                </p>
              </div>
            ) : null}

            <fieldset className="dealer-boost-form__section dealer-boost-form__section--review">
              <legend>{t("sponsorships.stepReview")}</legend>
              <label>
                {t("sponsorships.requestNote")}
                <textarea
                  rows={3}
                  value={requestMessage}
                  onChange={(event) => setRequestMessage(event.target.value)}
                  placeholder={t("sponsorships.requestPlaceholder")}
                />
              </label>

              <button
                type="submit"
                className="dealer-portal-btn primary dealer-boost-form__submit"
                disabled={submitting || boostableListings.length === 0}
              >
                {submitting
                  ? t("sponsorships.submitting")
                  : t("sponsorships.requestBoost", { price: formatMoney(newBoostEstimateCop) })}
              </button>
              {!featuredEligible ? <p className="muted">{upgradeMessage}</p> : null}
              {requestStatus ? (
                <p className={requestStatus.includes("[MISSING") ? "muted" : "dealer-sponsorships-page__status"}>
                  {requestStatus}
                </p>
              ) : null}
            </fieldset>
          </form>
        </section>

        <aside className="dealer-sponsorships-workspace__side">
          <section className="dealer-panel">
            <div className="dealer-panel__header">
              <h3>{t("sponsorships.activeBoostsTitle")}</h3>
            </div>
            {activePlacements.length === 0 ? (
              <p className="muted">{t("sponsorships.noActiveBoosts")}</p>
            ) : (
              <ul className="dealer-sponsorship-list">
                {activePlacements.map((placement) => (
                  <li key={placement.id} className="dealer-sponsorship-item">
                    <strong>{placement.listingTitle || placement.placementName}</strong>
                    <p className="muted">
                      {boostTypeLabel(t, placement.placementType, placement.placementType)} ·{" "}
                      {formatDateRange(placement.startAt, placement.endAt, locale)}
                    </p>
                    <p className="dealer-sponsorship-item__cost">
                      {t("sponsorships.costRemaining", { price: formatMoney(placement.costRemainingCop || 0) })}
                    </p>
                    <div className="dashboard-lead-stats">
                      <span>
                        {t("reports.impressions")}: {placement.performance?.impressions ?? 0}
                      </span>
                      <span>
                        {t("reports.clicks")}: {placement.performance?.clicks ?? 0}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {scheduledPlacements.length > 0 ? (
            <section className="dealer-panel dealer-panel--muted">
              <h3>{t("sponsorships.scheduledTitle")}</h3>
              <ul className="dealer-checklist">
                {scheduledPlacements.map((placement) => (
                  <li key={placement.id}>
                    {placement.listingTitle || placement.placementName} ·{" "}
                    {boostTypeLabel(t, placement.placementType, placement.placementType)}
                    <span className="dealer-sponsorship-item__cost-inline">
                      {t("sponsorships.costTotal", { price: formatMoney(placement.costTotalCop || 0) })}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {pendingRequests.length > 0 ? (
            <section className="dealer-panel">
              <h3>{t("sponsorships.pendingRequestsTitle")}</h3>
              <ul className="dealer-checklist">
                {pendingRequests.map((request) => (
                  <li key={request.id}>
                    {request.listingTitle || request.listingId} ·{" "}
                    {boostTypeLabel(t, request.requestedPlacementType, request.requestedPlacementType)}
                    <span className="dealer-sponsorship-item__cost-inline">
                      {t("sponsorships.costEstimated", { price: formatMoney(request.estimatedCostCop || 0) })}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {recentPlacements.length > 0 ? (
            <section className="dealer-panel dealer-panel--muted">
              <h3>{t("sponsorships.recentTitle")}</h3>
              <ul className="dealer-checklist dealer-checklist--compact">
                {recentPlacements.map((placement) => (
                  <li key={placement.id}>
                    {placement.listingTitle || placement.placementName} ·{" "}
                    {formatPlacementStatus(placement.runtimeStatus)}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </aside>
      </div>

      <details className="dealer-panel dealer-sponsorships-marketing">
        <summary>{t("sponsorships.marketingToggle")}</summary>
        <div className="dealer-sponsorships-marketing__body">
          <p className="dealer-sponsorships-page__eyebrow">{t("sponsorships.marketingEyebrow")}</p>
          <h3>{t("sponsorships.marketingTitle")}</h3>
          <p className="dealer-sponsorships-page__marketing-body">{t("sponsorships.marketingBody")}</p>
          <ul className="dealer-sponsorships-page__marketing-list">
            <li>{t("sponsorships.marketingPoint1")}</li>
            <li>{t("sponsorships.marketingPoint2")}</li>
            <li>{t("sponsorships.marketingPoint3")}</li>
          </ul>
        </div>
      </details>

      <section className="dealer-panel dealer-sponsorships-insights">
        <div className="dealer-panel__header">
          <div>
            <h3>{t("sponsorships.insightsTitle")}</h3>
            <p className="muted">{t("sponsorships.popularHint")}</p>
          </div>
        </div>

        <div className="dealer-sponsorships-insights__section">
          <h4 className="dealer-sponsorships-insights__subtitle">{t("sponsorships.popularTitle")}</h4>
          {popularListings.length === 0 ? (
            <p className="muted">{t("sponsorships.popularEmpty")}</p>
          ) : (
            <div className="import-table-wrap">
              <table className="dealer-inventory-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t("inventory.vehicle")}</th>
                    <th>{t("sponsorships.likes")}</th>
                    <th>{t("reports.listingViews")}</th>
                    <th>{t("sponsorships.boostStatus")}</th>
                    <th>{t("inventory.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {popularListings.map((listing) => (
                    <tr key={listing.listingId}>
                      <td>{listing.rank}</td>
                      <td>
                        <div className="dealer-popular-vehicle">
                          {listing.primaryPhotoUrl ? (
                            <img src={listing.primaryPhotoUrl} alt="" className="dealer-top-listing__thumb" />
                          ) : (
                            <div className="dealer-top-listing__thumb dealer-top-listing__thumb--empty" />
                          )}
                          <div>
                            <strong>{listing.vehicle}</strong>
                            <p className="muted">
                              {formatMoney(listing.priceCop)} · {listing.city || "—"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="dealer-like-count" aria-label={t("sponsorships.likes")}>
                          ♥ {listing.saveCount}
                        </span>
                      </td>
                      <td>{listing.listingViews ?? 0}</td>
                      <td>
                        {listing.isSponsored ? (
                          <span className={placementStatusClass(listing.sponsorshipStatus)}>
                            {formatPlacementStatus(listing.sponsorshipStatus)}
                          </span>
                        ) : (
                          <span className="muted">{t("sponsorships.notBoosted")}</span>
                        )}
                      </td>
                      <td>
                        <div className="dealer-inline-actions">
                          <Link className="dealer-portal-btn" to={listing.editPath}>
                            {t("common.edit")}
                          </Link>
                          {listing.canBoost ? (
                            <button
                              type="button"
                              className="dealer-portal-btn primary"
                              onClick={() => selectVehicle(listing.listingId)}
                            >
                              {t("sponsorships.boost")}
                            </button>
                          ) : (
                            <span className="muted dealer-sponsorships-page__inactive-hint">
                              {t("sponsorships.cannotBoostInactive")}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
